import React from 'react';
import adminApi, { clearAdminToken } from '../api/adminClient';
import withRouter from '../withRouter';

const emptyProductForm = {
  name: '', vendor: '', description: '', category: '',
  full_price: '', shared_price: '', shared_pool_size: 5, image_url: ''
};

class AdminDashboardPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      products: [],
      orders: [],
      loading: true,
      productForm: { ...emptyProductForm },
      creating: false,
      createError: null,

      selectedProductId: null,
      editForm: null,
      editMessage: null,
      savingEdit: false,
      privateKeysText: '',
      addingKeys: false,
      sharedPoolKey: '',
      sharedPoolSeats: 5,
      addingPool: false,
      stockMessage: null,

      view: 'products' // 'products' | 'orders'
    };
  }

  componentDidMount() {
    this.loadAll();
  }

  loadAll() {
    Promise.all([adminApi.getProducts(), adminApi.getOrders()])
      .then(([products, orders]) => this.setState({ products, orders, loading: false }))
      .catch(() => this.setState({ loading: false }));
  }

  handleLogout = () => {
    clearAdminToken();
    this.props.navigate('/admin/login');
  };

  handleProductFieldChange = (field, value) => {
    this.setState((prev) => ({ productForm: { ...prev.productForm, [field]: value } }));
  };

  handleCreateProduct = (e) => {
    e.preventDefault();
    this.setState({ creating: true, createError: null });
    const form = this.state.productForm;

    adminApi.createProduct({
      ...form,
      full_price: Number(form.full_price),
      shared_price: Number(form.shared_price),
      shared_pool_size: Number(form.shared_pool_size)
    })
      .then(() => {
        this.setState({ productForm: { ...emptyProductForm }, creating: false });
        this.loadAll();
      })
      .catch((err) => {
        this.setState({ creating: false, createError: err.response?.data?.error || 'Could not create product' });
      });
  };

  handleAddPrivateKeys = (productId) => {
    const keys = this.state.privateKeysText.split('\n').map(k => k.trim()).filter(Boolean);
    if (!keys.length) return;

    this.setState({ addingKeys: true, stockMessage: null });
    adminApi.addLicenseKeys(productId, keys)
      .then((res) => {
        this.setState({ addingKeys: false, privateKeysText: '', stockMessage: `Added ${res.added} private key(s).` });
        this.loadAll();
      })
      .catch((err) => {
        this.setState({ addingKeys: false, stockMessage: err.response?.data?.error || 'Failed to add keys' });
      });
  };

  handleAddSharedPool = (productId) => {
    const { sharedPoolKey, sharedPoolSeats } = this.state;
    if (!sharedPoolKey) return;

    this.setState({ addingPool: true, stockMessage: null });
    adminApi.addLicensePool(productId, sharedPoolKey, Number(sharedPoolSeats))
      .then(() => {
        this.setState({ addingPool: false, sharedPoolKey: '', stockMessage: 'Shared pool added.' });
        this.loadAll();
      })
      .catch((err) => {
        this.setState({ addingPool: false, stockMessage: err.response?.data?.error || 'Failed to add pool' });
      });
  };

  toggleActive = (product) => {
    adminApi.updateProduct(product.id, { is_active: !product.is_active }).then(() => this.loadAll());
  };

  toggleFeatured = (product) => {
    adminApi.updateProduct(product.id, { is_featured: !product.is_featured }).then(() => this.loadAll());
  };

  handleSelectProduct = (product) => {
    this.setState({
      selectedProductId: product.id,
      stockMessage: null,
      editForm: {
        name: product.name,
        vendor: product.vendor,
        description: product.description || '',
        category: product.category || '',
        full_price: product.full_price,
        shared_price: product.shared_price,
        shared_pool_size: product.shared_pool_size,
        image_url: product.image_url || ''
      },
      editMessage: null
    });
  };

  handleEditFieldChange = (field, value) => {
    this.setState((prev) => ({ editForm: { ...prev.editForm, [field]: value } }));
  };

  handleSaveEdit = (productId) => {
    const f = this.state.editForm;
    this.setState({ savingEdit: true, editMessage: null });
    adminApi.updateProduct(productId, {
      ...f,
      full_price: Number(f.full_price),
      shared_price: Number(f.shared_price),
      shared_pool_size: Number(f.shared_pool_size)
    })
      .then(() => {
        this.setState({ savingEdit: false, editMessage: 'Saved.' });
        this.loadAll();
      })
      .catch((err) => {
        this.setState({ savingEdit: false, editMessage: err.response?.data?.error || 'Could not save changes' });
      });
  };

  render() {
    const { products, orders, loading, productForm, creating, createError, view,
      selectedProductId, privateKeysText, addingKeys, sharedPoolKey, sharedPoolSeats, addingPool, stockMessage } = this.state;

    if (loading) return <p className="loading">Loading admin dashboard...</p>;
    const selectedProduct = products.find(p => p.id === selectedProductId);

    return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <h1>Admin dashboard</h1>
          <button className="btn" onClick={this.handleLogout}>Log out</button>
        </div>

        <div className="admin-tabs">
          <button className={view === 'products' ? 'tab active' : 'tab'} onClick={() => this.setState({ view: 'products' })}>Products & stock</button>
          <button className={view === 'orders' ? 'tab active' : 'tab'} onClick={() => this.setState({ view: 'orders' })}>Recent orders</button>
        </div>

        {view === 'products' && (
          <>
            <section className="admin-section">
              <h2>Add a new product</h2>
              <form className="admin-form" onSubmit={this.handleCreateProduct}>
                <input placeholder="Name" required value={productForm.name}
                  onChange={(e) => this.handleProductFieldChange('name', e.target.value)} />
                <input placeholder="Vendor (e.g. Microsoft)" required value={productForm.vendor}
                  onChange={(e) => this.handleProductFieldChange('vendor', e.target.value)} />
                <textarea placeholder="Description" value={productForm.description}
                  onChange={(e) => this.handleProductFieldChange('description', e.target.value)} />
                <input placeholder="Category" value={productForm.category}
                  onChange={(e) => this.handleProductFieldChange('category', e.target.value)} />
                <input placeholder="Image URL" value={productForm.image_url}
                  onChange={(e) => this.handleProductFieldChange('image_url', e.target.value)} />
                <input type="number" placeholder="Private price (₦)" required value={productForm.full_price}
                  onChange={(e) => this.handleProductFieldChange('full_price', e.target.value)} />
                <input type="number" placeholder="Shared price (₦)" required value={productForm.shared_price}
                  onChange={(e) => this.handleProductFieldChange('shared_price', e.target.value)} />
                <input type="number" placeholder="Shared pool size" value={productForm.shared_pool_size}
                  onChange={(e) => this.handleProductFieldChange('shared_pool_size', e.target.value)} />
                {createError && <p className="error">{createError}</p>}
                <button className="btn btn-primary" type="submit" disabled={creating}>
                  {creating ? 'Creating...' : 'Create product'}
                </button>
              </form>
            </section>

            <section className="admin-section">
              <h2>Products & license stock</h2>
              <p className="hint">Click "Featured" to control which licenses show under "Popular licenses" on the homepage.</p>
              <table className="admin-table">
                <thead>
                  <tr><th>Name</th><th>Active</th><th>Featured</th><th>Private keys left</th><th>Open shared seats</th><th></th></tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id}>
                      <td>{p.name}</td>
                      <td>
                        <button className="btn-link" onClick={() => this.toggleActive(p)}>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td>
                        <button className="btn-link" onClick={() => this.toggleFeatured(p)}>
                          {p.is_featured ? '★ Featured' : '☆ Not featured'}
                        </button>
                      </td>
                      <td>{p.available_private_keys}</td>
                      <td>{p.open_shared_seats}</td>
                      <td>
                        <button className="btn-link" onClick={() => this.handleSelectProduct(p)}>
                          Manage stock
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {selectedProduct && (
              <section className="admin-section">
                <h2>Edit details — {selectedProduct.name}</h2>
                <form className="admin-form" onSubmit={(e) => { e.preventDefault(); this.handleSaveEdit(selectedProduct.id); }}>
                  <input placeholder="Name" value={this.state.editForm.name}
                    onChange={(e) => this.handleEditFieldChange('name', e.target.value)} />
                  <input placeholder="Vendor" value={this.state.editForm.vendor}
                    onChange={(e) => this.handleEditFieldChange('vendor', e.target.value)} />
                  <textarea placeholder="Description" value={this.state.editForm.description}
                    onChange={(e) => this.handleEditFieldChange('description', e.target.value)} />
                  <input placeholder="Category" value={this.state.editForm.category}
                    onChange={(e) => this.handleEditFieldChange('category', e.target.value)} />
                  <input placeholder="Image URL (e.g. /product-images/pm2io.svg)" value={this.state.editForm.image_url}
                    onChange={(e) => this.handleEditFieldChange('image_url', e.target.value)} />
                  <input type="number" placeholder="Private price (₦)" value={this.state.editForm.full_price}
                    onChange={(e) => this.handleEditFieldChange('full_price', e.target.value)} />
                  <input type="number" placeholder="Shared price (₦)" value={this.state.editForm.shared_price}
                    onChange={(e) => this.handleEditFieldChange('shared_price', e.target.value)} />
                  <input type="number" placeholder="Shared pool size" value={this.state.editForm.shared_pool_size}
                    onChange={(e) => this.handleEditFieldChange('shared_pool_size', e.target.value)} />
                  {this.state.editMessage && <p className="stock-message">{this.state.editMessage}</p>}
                  <button className="btn btn-primary" type="submit" disabled={this.state.savingEdit}>
                    {this.state.savingEdit ? 'Saving...' : 'Save changes'}
                  </button>
                </form>
              </section>
            )}

            {selectedProduct && (
              <section className="admin-section">
                <h2>Manage stock — {selectedProduct.name}</h2>
                {stockMessage && <p className="stock-message">{stockMessage}</p>}

                <div className="stock-forms">
                  <div className="stock-form">
                    <h3>Add private license keys</h3>
                    <p className="hint">One key per line. Each becomes a single, unassigned private license.</p>
                    <textarea
                      rows={5}
                      placeholder={'ABCD-1234-EFGH-5678\nWXYZ-9999-AAAA-1111'}
                      value={privateKeysText}
                      onChange={(e) => this.setState({ privateKeysText: e.target.value })}
                    />
                    <button className="btn btn-primary" disabled={addingKeys}
                      onClick={() => this.handleAddPrivateKeys(selectedProduct.id)}>
                      {addingKeys ? 'Adding...' : 'Add keys'}
                    </button>
                  </div>

                  <div className="stock-form">
                    <h3>Open a shared license pool</h3>
                    <p className="hint">One multi-seat key, shared by up to N buyers at the discounted price.</p>
                    <input
                      placeholder="Multi-seat license key"
                      value={sharedPoolKey}
                      onChange={(e) => this.setState({ sharedPoolKey: e.target.value })}
                    />
                    <input
                      type="number"
                      min="1"
                      placeholder="Max seats"
                      value={sharedPoolSeats}
                      onChange={(e) => this.setState({ sharedPoolSeats: e.target.value })}
                    />
                    <button className="btn btn-primary" disabled={addingPool}
                      onClick={() => this.handleAddSharedPool(selectedProduct.id)}>
                      {addingPool ? 'Adding...' : 'Add pool'}
                    </button>
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {view === 'orders' && (
          <section className="admin-section">
            <h2>Recent orders</h2>
            <table className="admin-table">
              <thead>
                <tr><th>Reference</th><th>Email</th><th>Total</th><th>Status</th><th>Provider</th><th>Date</th></tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id}>
                    <td>{o.payment_reference}</td>
                    <td>{o.email}</td>
                    <td>₦{Number(o.total_amount).toLocaleString()}</td>
                    <td>{o.status}</td>
                    <td>{o.payment_provider}</td>
                    <td>{new Date(o.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    );
  }
}

export default withRouter(AdminDashboardPage);
